import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { testimonialsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Plus,
  Pencil,
  Trash2,
  Quote,
  GripVertical,
  Eye,
  EyeOff,
  Loader2,
  Image as ImageIcon
} from 'lucide-react';

export default function AdminTestimonialsPage() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    quote: '',
    image_url: '',
    is_active: true,
    order: 0
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    try {
      const response = await testimonialsAPI.getAll();
      setTestimonials(response.data);
    } catch (error) {
      console.error('Failed to load testimonials:', error);
      // If no testimonials, try to seed defaults
      if (error.response?.status === 404 || (Array.isArray(error.response?.data) && error.response?.data.length === 0)) {
        try {
          await testimonialsAPI.seed();
          const retryResponse = await testimonialsAPI.getAll();
          setTestimonials(retryResponse.data);
        } catch (seedError) {
          console.error('Failed to seed testimonials:', seedError);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingTestimonial(null);
    setFormData({
      name: '',
      role: '',
      quote: '',
      image_url: '',
      is_active: true,
      order: testimonials.length
    });
    setShowModal(true);
  };

  const handleOpenEdit = (testimonial) => {
    setEditingTestimonial(testimonial);
    setFormData({
      name: testimonial.name,
      role: testimonial.role,
      quote: testimonial.quote,
      image_url: testimonial.image_url || '',
      is_active: testimonial.is_active,
      order: testimonial.order
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.role || !formData.quote) {
      toast.error('Please fill in name, role, and quote');
      return;
    }

    setSubmitting(true);
    try {
      if (editingTestimonial) {
        await testimonialsAPI.update(editingTestimonial.id, formData);
        toast.success('Testimonial updated');
      } else {
        await testimonialsAPI.create(formData);
        toast.success('Testimonial created');
      }
      setShowModal(false);
      loadTestimonials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save testimonial');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) {
      return;
    }

    try {
      await testimonialsAPI.delete(id);
      toast.success('Testimonial deleted');
      loadTestimonials();
    } catch (error) {
      toast.error('Failed to delete testimonial');
    }
  };

  const handleToggleActive = async (testimonial) => {
    try {
      await testimonialsAPI.update(testimonial.id, {
        is_active: !testimonial.is_active
      });
      loadTestimonials();
      toast.success(testimonial.is_active ? 'Testimonial hidden' : 'Testimonial visible');
    } catch (error) {
      toast.error('Failed to update testimonial');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#1A3C34]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="admin-testimonials-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-serif text-[#1A3C34]">Testimonials</h1>
            <p className="text-[#1A3C34]/60 mt-1">
              Manage testimonials shown on the landing page
            </p>
          </div>
          <Button
            onClick={handleOpenCreate}
            className="bg-[#1A3C34] hover:bg-[#1A3C34]/90 text-white rounded-xl"
            data-testid="add-testimonial-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Testimonial
          </Button>
        </div>

        {/* Testimonials List */}
        <div className="space-y-4">
          {testimonials.length === 0 ? (
            <div className="bg-white rounded-2xl border border-[#1A3C34]/10 p-12 text-center">
              <Quote className="w-12 h-12 text-[#1A3C34]/20 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-[#1A3C34] mb-2">No testimonials yet</h3>
              <p className="text-[#1A3C34]/60 mb-6">Add your first testimonial to display on the landing page</p>
              <Button onClick={handleOpenCreate} className="bg-[#1A3C34] text-white rounded-xl">
                <Plus className="w-4 h-4 mr-2" />
                Add Testimonial
              </Button>
            </div>
          ) : (
            testimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                className={`bg-white rounded-2xl border ${testimonial.is_active ? 'border-[#1A3C34]/10' : 'border-[#D4A5A5]/30 bg-[#D4A5A5]/5'} p-6 transition-all hover:shadow-md`}
                data-testid={`testimonial-item-${index}`}
              >
                <div className="flex items-start gap-4">
                  {/* Drag Handle & Order */}
                  <div className="flex flex-col items-center gap-2 pt-1">
                    <GripVertical className="w-5 h-5 text-[#1A3C34]/30" />
                    <span className="text-xs text-[#1A3C34]/40">#{testimonial.order + 1}</span>
                  </div>

                  {/* Image */}
                  <div className="flex-shrink-0">
                    {testimonial.image_url ? (
                      <img
                        src={testimonial.image_url}
                        alt={testimonial.name}
                        className="w-16 h-16 rounded-full object-cover border-2 border-[#1A3C34]/10"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-[#1A3C34]/10 flex items-center justify-center">
                        <ImageIcon className="w-6 h-6 text-[#1A3C34]/40" />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-semibold text-[#1A3C34]">{testimonial.name}</h3>
                        <p className="text-sm text-[#1A3C34]/60">{testimonial.role}</p>
                      </div>
                      {!testimonial.is_active && (
                        <span className="px-2 py-1 bg-[#D4A5A5]/20 text-[#D4A5A5] text-xs font-medium rounded-full">
                          Hidden
                        </span>
                      )}
                    </div>
                    <blockquote className="mt-3 text-[#1A3C34]/70 italic border-l-2 border-[#D4A5A5]/30 pl-4">
                      "{testimonial.quote}"
                    </blockquote>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleActive(testimonial)}
                      className="text-[#1A3C34]/60 hover:text-[#1A3C34]"
                      title={testimonial.is_active ? 'Hide testimonial' : 'Show testimonial'}
                    >
                      {testimonial.is_active ? (
                        <Eye className="w-4 h-4" />
                      ) : (
                        <EyeOff className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenEdit(testimonial)}
                      className="text-[#1A3C34]/60 hover:text-[#1A3C34]"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(testimonial.id)}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Create/Edit Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-lg bg-white rounded-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl font-serif text-[#1A3C34]">
                {editingTestimonial ? 'Edit Testimonial' : 'Add Testimonial'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-5 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-[#1A3C34]">Name *</Label>
                  <Input
                    id="name"
                    placeholder="Emma Richardson"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="rounded-xl border-[#1A3C34]/10"
                    required
                    data-testid="testimonial-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role" className="text-[#1A3C34]">Role *</Label>
                  <Input
                    id="role"
                    placeholder="First-time Mom"
                    value={formData.role}
                    onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                    className="rounded-xl border-[#1A3C34]/10"
                    required
                    data-testid="testimonial-role-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="quote" className="text-[#1A3C34]">Quote *</Label>
                <Textarea
                  id="quote"
                  placeholder="Share their experience..."
                  value={formData.quote}
                  onChange={(e) => setFormData(prev => ({ ...prev, quote: e.target.value }))}
                  className="rounded-xl border-[#1A3C34]/10 min-h-[100px]"
                  required
                  data-testid="testimonial-quote-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="image_url" className="text-[#1A3C34]">Image URL</Label>
                <Input
                  id="image_url"
                  type="url"
                  placeholder="https://example.com/photo.jpg"
                  value={formData.image_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                  className="rounded-xl border-[#1A3C34]/10"
                  data-testid="testimonial-image-input"
                />
                <p className="text-xs text-[#1A3C34]/50">Recommended: Square image, 100x100px minimum</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="order" className="text-[#1A3C34]">Display Order</Label>
                  <Input
                    id="order"
                    type="number"
                    min="0"
                    value={formData.order}
                    onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                    className="rounded-xl border-[#1A3C34]/10"
                    data-testid="testimonial-order-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1A3C34]">Visibility</Label>
                  <div className="flex items-center gap-3 pt-2">
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                      data-testid="testimonial-active-switch"
                    />
                    <span className="text-sm text-[#1A3C34]/70">
                      {formData.is_active ? 'Visible on landing page' : 'Hidden'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowModal(false)}
                  className="flex-1 rounded-xl border-[#1A3C34]/20"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-[#1A3C34] hover:bg-[#1A3C34]/90 text-white rounded-xl"
                  data-testid="testimonial-submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    editingTestimonial ? 'Update' : 'Create'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
